import { IonCard, IonIcon } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { type ShiftTimeEntry } from "@/lib/backend/api";
import { xMarkIcon } from "@/lib/icons/@heroicons/react/20/solid";
import { checkIcon } from "@/lib/icons/check.icon";
import { hourglassIcon } from "@/lib/icons/hourglass.icon";
import { timeIcon } from "@/lib/icons/time.icon";

type ShiftTimeEntryStatus = "PENDING" | "ACCEPTED" | "REJECTED";

interface ShiftStatusCardProps {
  allowEmployeeSubmitHours?: boolean;
  status?: ShiftTimeEntryStatus | null;
  shiftTimeEntry?: ShiftTimeEntry | null;
}

export function ShiftStatusCard({
  shiftTimeEntry,
  allowEmployeeSubmitHours = true,
  status,
}: ShiftStatusCardProps) {
  const { t } = useTranslation();

  const hasShiftTimeEntry = !!shiftTimeEntry?.id;
  const hasStartTime = !!shiftTimeEntry?.startAt;
  const hasEndTime = !!shiftTimeEntry?.endAt;
  const hasBreakTime = shiftTimeEntry?.breakTimeMinutes !== null;

  const { title, description } = (() => {
    if (!hasShiftTimeEntry) {
      return {
        title: t("common:shifts.no-hours-submitted.title"),
        description: t("common:shifts.no-hours-submitted.description"),
      };
    }

    if (!hasStartTime) {
      return {
        title: t("common:shifts.no-start-time.title"),
        description: t("common:shifts.no-start-time.description"),
      };
    }

    if (!hasEndTime) {
      return {
        title: t("common:shifts.no-end-time.title"),
        description: t("common:shifts.no-end-time.description"),
      };
    }

    if (!hasBreakTime) {
      return {
        title: t("common:shifts.no-break-time.title"),
        description: t("common:shifts.no-break-time.description"),
      };
    }

    return { title: "", description: "" };
  })();

  if (title && description) {
    return (
      <IonCard className="flex items-center gap-4 p-4">
        <IonIcon
          icon={timeIcon}
          className="size-5 shrink-0 rounded-full bg-surface-secondary p-2 text-brand-500"
        />
        <div className="flex flex-col gap-0.5">
          <strong>{title}</strong>
          <p className="text-sm font-medium text-txt-secondary">{description}</p>
        </div>
      </IonCard>
    );
  }

  if (!hasShiftTimeEntry && allowEmployeeSubmitHours) {
    return (
      <IonCard className="flex items-center gap-4 p-4">
        <IonIcon
          icon={timeIcon}
          className="size-5 shrink-0 rounded-full bg-surface-secondary p-2 text-brand-500"
        />
        <div className="flex flex-col gap-0.5">
          <strong>{t("common:shifts.no-hours-submitted.title")}</strong>
          <p className="text-sm font-medium text-txt-secondary">
            {t("common:shifts.no-hours-submitted.description")}
          </p>
        </div>
      </IonCard>
    );
  }

  // If employee can't submit hours, use the no-submit-hours translations
  const translationPrefix = allowEmployeeSubmitHours
    ? "enums:shiftTimeEntryStatus"
    : "enums:shiftTimeEntryStatusNoSubmitHours";

  const effectiveStatus = status ?? "PENDING";

  return (
    <IonCard className="flex items-center gap-4 p-4">
      <IonIcon
        icon={
          effectiveStatus === "PENDING"
            ? hourglassIcon
            : effectiveStatus === "ACCEPTED"
              ? checkIcon
              : xMarkIcon
        }
        className="size-5 shrink-0 rounded-full bg-surface-secondary p-2 text-brand-500"
      />
      <div className="flex flex-col gap-0.5">
        <strong>{t(`${translationPrefix}.${effectiveStatus}.title`)}</strong>
        <p className="text-sm font-medium text-txt-secondary">
          {t(`${translationPrefix}.${effectiveStatus}.description`)}
        </p>
      </div>
    </IonCard>
  );
}
